import { Fragment } from 'react'
import { PENDO_IGNORE } from '@/analytics/domPrivacy'

/**
 * Renders the data section of a CB Report — visible columns + rows
 * (including grouped rows + group footers + report-level footer).
 *
 * The CB Report shape (deep-camelized):
 *   columns: [{ columnId, label, visible, columnAlignment, columnWidth, dataType,
 *               cellsFormat, cellsFormatDecimals, ... }]
 *   reportData: [
 *     { isGroup: false, dataCells: [{ columnId, displayValue, dataValue, color }] },
 *     { isGroup: true,  groupHeaderContent, groupFooterContent, groupHeaderCSSClass,
 *       dataRows: [...recursively...], groupFooterCells: [{columnId, displayValue}] }
 *   ]
 *   reportFooterCells: [{ columnId, displayValue }]
 */

const colorToCss = (c) => {
  if (!c || typeof c !== 'object') return null
  const r = c.r ?? c.R
  const g = c.g ?? c.G
  const b = c.b ?? c.B
  const a = c.a ?? c.A ?? 255
  if (r == null || g == null || b == null) return null
  return `rgba(${r}, ${g}, ${b}, ${(a / 255).toFixed(2)})`
}

const alignClass = (col) => {
  switch (col.columnAlignment) {
    case 'RIGHT': return 'text-right'
    case 'CENTER': return 'text-center'
    case 'LEFT':
    default: return 'text-left'
  }
}

const headerAlignClass = (col) => {
  switch (col.columnHeaderAlign) {
    case 'RIGHT': return 'text-right'
    case 'CENTER': return 'text-center'
    case 'LEFT':
    default: return alignClass(col)
  }
}

const widthStyle = (col) => {
  if (!col.columnWidth) return undefined
  // CB stores widths as e.g. "10%" or "120px"
  return { width: col.columnWidth }
}

const Cell = ({ col, cell }) => {
  const bg = colorToCss(cell?.color)
  const fg = colorToCss(col?.color) // less common
  return (
    <td
      className={`${PENDO_IGNORE} px-3 py-2 text-sm text-gray-800 border-b border-gray-100 ${alignClass(col)}`}
      style={{
        ...(bg ? { backgroundColor: bg } : {}),
        ...(fg ? { color: fg } : {}),
      }}
      // eslint-disable-next-line react/no-danger
      dangerouslySetInnerHTML={{ __html: cell?.displayValue ?? '' }}
    />
  )
}

const cellByColumnId = (cells, columnId) =>
  cells?.find((c) => c.columnId === columnId)

/** Recursive row renderer. Handles plain rows, group headers, sub-groups, and group footers. */
const Rows = ({ rows = [], visibleColumns, level = 0 }) => {
  const colSpan = visibleColumns.length
  return (
    <>
      {rows.map((row, idx) => {
        if (!row.isGroup) {
          return (
            <tr key={`r-${level}-${idx}`} className="hover:bg-gray-50">
              {visibleColumns.map((col) => (
                <Cell key={col.columnId} col={col} cell={cellByColumnId(row.dataCells, col.columnId)} />
              ))}
            </tr>
          )
        }
        // Group row
        return (
          <Fragment key={`g-${level}-${idx}`}>
            <tr className="bg-gray-50">
              <td
                colSpan={colSpan}
                className={`${PENDO_IGNORE} px-3 py-2 text-sm font-semibold text-gray-700 border-b border-gray-200`}
                style={{ paddingLeft: `${12 + level * 16}px` }}
                // eslint-disable-next-line react/no-danger
                dangerouslySetInnerHTML={{ __html: row.groupHeaderContent || '' }}
              />
            </tr>
            <Rows rows={row.dataRows} visibleColumns={visibleColumns} level={level + 1} />
            {row.groupFooterCells && row.groupFooterCells.length > 0 && (
              <tr className="bg-gray-50/50 font-medium">
                {visibleColumns.map((col) => {
                  const cell = cellByColumnId(row.groupFooterCells, col.columnId)
                  return (
                    <td
                      key={col.columnId}
                      className={`${PENDO_IGNORE} px-3 py-1.5 text-sm text-gray-800 border-b border-gray-200 ${alignClass(col)}`}
                      // eslint-disable-next-line react/no-danger
                      dangerouslySetInnerHTML={{ __html: cell?.displayValue ?? '' }}
                    />
                  )
                })}
              </tr>
            )}
            {row.groupFooterContent && (
              <tr className="bg-gray-50/50">
                <td
                  colSpan={colSpan}
                  className={`${PENDO_IGNORE} px-3 py-1.5 text-xs text-gray-600 italic border-b border-gray-200`}
                  style={{ paddingLeft: `${12 + level * 16}px` }}
                  // eslint-disable-next-line react/no-danger
                  dangerouslySetInnerHTML={{ __html: row.groupFooterContent }}
                />
              </tr>
            )}
          </Fragment>
        )
      })}
    </>
  )
}

const ReportTable = ({ report }) => {
  if (!report) return null
  const allColumns = report.columns || []
  const visibleColumns = allColumns.filter((c) => c.visible !== false && !c.isBlankColumn)
  const rows = report.reportData || []
  const footerCells = report.reportFooterCells || []
  const showFooter = footerCells.length > 0

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
      <div className="overflow-x-auto">
        <table className="min-w-full">
          <thead className="bg-gray-50">
            <tr>
              {visibleColumns.map((col) => (
                <th
                  key={col.columnId}
                  scope="col"
                  className={`px-3 py-2 text-xs font-semibold text-gray-600 uppercase tracking-wider border-b border-gray-200 ${headerAlignClass(col)}`}
                  style={widthStyle(col)}
                >
                  {col.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={visibleColumns.length} className="px-3 py-12 text-center text-sm text-gray-500">
                  No data for the selected filters
                </td>
              </tr>
            ) : (
              <Rows rows={rows} visibleColumns={visibleColumns} />
            )}
          </tbody>
          {showFooter && (
            <tfoot className="bg-gray-100 font-semibold">
              <tr>
                {visibleColumns.map((col) => {
                  const cell = footerCells.find((f) => f.columnId === col.columnId)
                  return (
                    <td
                      key={col.columnId}
                      className={`${PENDO_IGNORE} px-3 py-2 text-sm text-gray-900 border-t border-gray-200 ${alignClass(col)}`}
                      // eslint-disable-next-line react/no-danger
                      dangerouslySetInnerHTML={{ __html: cell?.displayValue ?? '' }}
                    />
                  )
                })}
              </tr>
            </tfoot>
          )}
        </table>
      </div>
      {report.recordCount != null && (
        <div className="px-4 py-2 text-xs text-gray-500 border-t border-gray-100 bg-gray-50/50">
          {report.recordCount} {report.recordCount === 1 ? 'record' : 'records'}
        </div>
      )}
    </div>
  )
}

export default ReportTable
